import React from "react";
import { Box, Typography, Fade, Grow } from "@mui/material";
import { styled, alpha, keyframes } from "@mui/material/styles";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import useApp from "../../../store/contexts/AppContext";
import capitalizeEachFirstWord from "../../../utils/CapLetters";

// Animations
const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

// Styled Components
const Container = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  overflow: "hidden",
  background: theme.palette.mode === "dark"
    ? theme.palette.background.default
    : theme.palette.background.paper,
}));

const ContentBox = styled(Box)(({ theme }) => ({
  textAlign: "center",
  padding: "40px",
  position: "relative",
  zIndex: 1,
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: 140,
  height: 140,
  margin: "0 auto 32px",
  borderRadius: "50%",
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.2
  )}, ${alpha(theme.palette.primary.dark, 0.05)})`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  animation: `${float} 3s ease-in-out infinite`,
  backdropFilter: "blur(20px)",
  border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.2)}`,

  "&::before": {
    content: '""',
    position: "absolute",
    inset: -2,
    borderRadius: "50%",
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light || theme.palette.primary.main}, ${theme.palette.primary.main})`,
    backgroundSize: "200% 200%",
    animation: `${shimmer} 3s linear infinite`,
    opacity: 0.3,
    zIndex: -1,
  },
}));

const StyledChatIcon = styled(ChatBubbleOutlineIcon)(({ theme }) => ({
  fontSize: 64,
  color: theme.palette.primary.main,
  filter: `drop-shadow(0 4px 12px ${alpha(theme.palette.primary.main, 0.4)})`,
  animation: `${pulse} 2s ease-in-out infinite`,
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontSize: "32px",
  fontWeight: 700,
  marginBottom: "12px",
  background: theme.palette.mode === "dark"
    ? `linear-gradient(135deg, ${theme.palette.common.white}, ${alpha(
        theme.palette.common.white,
        0.7
      )})`
    : `linear-gradient(135deg, ${theme.palette.text.primary}, ${alpha(
        theme.palette.text.secondary,
        0.8
      )})`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  letterSpacing: "-0.02em",
}));

const SubText = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  color: theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.6)
    : alpha(theme.palette.text.secondary, 0.7),
  marginTop: "16px",
  lineHeight: 1.6,
}));

const BackgroundGlow = styled(Box)(({ theme }) => ({
  position: "absolute",
  width: "600px",
  height: "600px",
  borderRadius: "50%",
  background: `radial-gradient(circle, ${alpha(
    theme.palette.primary.main,
    0.15
  )}, transparent 70%)`,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  pointerEvents: "none",
  animation: `${pulse} 4s ease-in-out infinite`,
}));

export default function EmptyChatDashboard() {
  const {
    appState: { userData },
  } = useApp();

  return (
    <Container>
      <BackgroundGlow />

      <Fade in timeout={800}>
        <ContentBox>
          <Grow in timeout={1000}>
            <IconContainer>
              <StyledChatIcon />
            </IconContainer>
          </Grow>

          <Fade in timeout={1200}>
            <WelcomeText>Hi, {userData?.username ? capitalizeEachFirstWord(userData.username) : "there"}!</WelcomeText>
          </Fade>

          <Fade in timeout={1400}>
            <SubText>
              Select a conversation from the sidebar
              <br />
              to start messaging
            </SubText>
          </Fade>
        </ContentBox>
      </Fade>
    </Container>
  );
}
